import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";

/**
 * Money helpers:
 * - sanitizeToMoney: keep digits and one '.', clamp to 2 decimals (no commas here)
 * - formatMoney: add thousand separators, keep up to 2 decimals
 */
const sanitizeToMoney = (value) => {
  if (value == null) return "";
  let v = String(value).replace(/,/g, "").replace(/[^\d.]/g, "");

  const parts = v.split(".");
  if (parts.length > 2) {
    v = `${parts[0]}.${parts.slice(1).join("")}`;
  }

  let [intPart = "", decPart = ""] = v.split(".");
  const normalizedInt = intPart.replace(/^0+(?=\d)/, "");

  // Preserve trailing dot while typing
  if (v.endsWith(".")) {
    return `${normalizedInt || "0"}.`;
  }

  const clampedDec = (decPart || "").slice(0, 2);

  return clampedDec !== ""
    ? `${normalizedInt || "0"}.${clampedDec}`
    : (normalizedInt || "");
};

const formatMoney = (value) => {
  if (value == null || value === "") return "";

  const str = String(value);
  const hasDot = str.includes(".");
  const [intPartRaw, decPart = ""] = str.split(".");
  const intPart = intPartRaw === "" ? "0" : intPartRaw;

  const intWithCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (!hasDot) return intWithCommas;
  if (str.endsWith(".")) return `${intWithCommas}.`;
  return `${intWithCommas}.${decPart}`;
};

const EditClientModal = ({ show, onHide, onSave, client }) => {
  const [bpId, setBpId] = useState("");
  const [amountDisplay, setAmountDisplay] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const amountInputRef = useRef(null);

  useEffect(() => {
    if (show && client) {
      setBpId(client?.bpId === "To be Assigned" ? "" : client?.bpId ?? "");

      const initialAmount =
        client?.amount === "To be Assigned" || client?.amount == null || client?.amount === ""
          ? ""
          : formatMoney(sanitizeToMoney(String(client.amount)));

      setAmountDisplay(initialAmount);
      setErrors({});
      setSaving(false);
    }
  }, [show, client]);

  const validate = () => {
    const e = {};
    const bp = bpId?.trim();

    if (!bp) e.bpId = "BPID is required.";
    if (bp === "To be Assigned") e.bpId = 'Value cannot be "To be Assigned".';

    const amt = amountDisplay?.trim();
    if (!amt) {
      e.amount = "Amount is required.";
    } else {
      const raw = amt.replace(/,/g, "");
      const moneyRe = /^(?:\d+|\d+\.\d{1,2})$/;
      if (!moneyRe.test(raw)) {
        e.amount = "Amount must be numeric, with up to 2 decimal places.";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Caret-preserving formatter: keeps cursor position stable when commas change
  const handleAmountChange = (e) => {
    const input = e.target;
    const prev = input.value;
    const caret = input.selectionStart ?? prev.length;

    // How many digits are to the left of the caret right now?
    const leftPrev = prev.slice(0, caret);
    const digitsBefore = (leftPrev.match(/\d/g) || []).length;
    const hadDotBefore = leftPrev.includes(".");

    // Compute the new model & formatted value
    const sanitized = sanitizeToMoney(prev);
    const formatted = formatMoney(sanitized);

    // Helper: find index in formatted string after N digits (ignoring commas)
    const indexAfterNDigits = (formattedStr, nDigits) => {
      let count = 0;
      for (let i = 0; i < formattedStr.length; i++) {
        const ch = formattedStr[i];
        if (/\d/.test(ch)) {
          count++;
          if (count === nDigits) {
            return i + 1; // caret goes after this digit
          }
        }
      }
      return formattedStr.length;
    };

    // Figure out where the caret should land in the new formatted string
    let newPos = indexAfterNDigits(formatted, digitsBefore);

    // If the caret was after the dot before, ensure it stays after the dot
    const dotIndex = formatted.indexOf(".");
    if (hadDotBefore && dotIndex !== -1 && newPos <= dotIndex) {
      newPos = Math.min(formatted.length, dotIndex + 1);
    }

    setAmountDisplay(formatted);

    // Restore caret after React updates the input value
    requestAnimationFrame(() => {
      const el = amountInputRef.current;
      if (!el) return;
      el.setSelectionRange(newPos, newPos);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);

      await onSave({
        bpId: bpId.trim(),
        amount: amountDisplay, // Keep commas; send as string exactly as displayed
      });
    } catch (err) {
      console.error("Failed to save identifiers", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static" keyboard={!saving} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Edit Client Identifiers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Client:</strong> {client?.name ?? "-"}
          </div>

          <Form.Group className="mb-3" controlId="bpId">
            <Form.Label>BPID</Form.Label>
            <Form.Control
              value={bpId}
              onChange={(e) => setBpId(e.target.value)}
              placeholder="Enter BPID"
              isInvalid={!!errors.bpId}
              disabled={saving}
              autoComplete="off"
              minLength={11}
              maxLength={11}
            />
            <Form.Control.Feedback type="invalid">{errors.bpId}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-1" controlId="amount">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              ref={amountInputRef}
              inputMode="numeric"
              pattern="^[0-9,]*\.?[0-9]{0,2}$"
              value={amountDisplay}
              onChange={handleAmountChange}
              placeholder="Enter Amount"
              isInvalid={!!errors.amount}
              disabled={saving}
              autoComplete="off"
            />
            <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
            <Form.Text muted>
              Numbers only, auto‑comma, maximum 2 decimals. Example: 1,234.56
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" /> Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditClientModal;